import { Button, Spinner } from "@instacover/ui"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useCallback } from "react"
import { Trans } from "react-i18next"

import IconRefresh from "@/assets/icons/IconRefresh.svg?react"
import { db } from "@/database/database"
import { photoStepSearchSchema } from "@/helpers/zodSchemes"
import { LogoHeader } from "@/sections/layouts/LogoHeader"

import { AmenitiesGroup } from "./-AmenitiesGroup"
import PhotoDetailOverview from "./-PhotoDetailOverview"
import { UploadStatusBanner } from "./-UploadStatusBanner"
import { OverviewUploadMode, useOverviewUpload } from "./-useOverviewUpload"
import { WallSection } from "./-WallSection"

export const Route = createFileRoute("/$sessionId/_session/_steps/overview/")({
  component: OverviewPage,
  validateSearch: (search) => photoStepSearchSchema.parse(search),
})

function OverviewPage() {
  const { sessionId } = Route.useParams()
  const { currentPhotoId } = Route.useSearch()
  const navigate = useNavigate()

  /* Upload state */
  const { mode, onRetry } = useOverviewUpload(sessionId)

  /* Finish session */
  const onFinish = useCallback(async () => {
    const nextStep = await db.orderedStep.finishAndGetNext({ step: "Overview", sessionId })
    if (nextStep) {
      navigate({ to: nextStep.to, params: { ...nextStep.params } })
    }
  }, [navigate, sessionId])

  if (currentPhotoId) {
    return <PhotoDetailOverview currentPhotoId={currentPhotoId} />
  }

  return (
    <>
      <main>
        <LogoHeader className="my-4" />

        <UploadStatusBanner mode={mode} onRetry={onRetry} />

        <div>
          <WallSection sessionId={sessionId} />

          {/* ExtraEquipment */}
          <AmenitiesGroup
            photoType="ExtraEquipment"
            sessionId={sessionId}
            sectionDataTid="overview-extra-equipment-section"
          />

          {/* UtilityRoom */}
          <AmenitiesGroup
            photoType="UtilityRoom"
            sessionId={sessionId}
            sectionDataTid="overview-utility-room-section"
            dividerBottom={false}
          />
        </div>
      </main>

      {mode !== "offline" && (
        <div className="ic-z-sticky sticky bottom-4 mt-11 flex justify-end px-4 pb-[env(safe-area-inset-bottom)]">
          <FinishButton mode={mode} onRetry={onRetry} onFinish={onFinish} />
        </div>
      )}
    </>
  )
}

type FinishButtonProps = {
  mode: OverviewUploadMode
  onRetry: () => void
  onFinish: () => void
}

function FinishButton(props: FinishButtonProps) {
  const { mode, onRetry, onFinish } = props

  if (mode === "failed") {
    return (
      <Button
        variant="primary"
        shape="wide"
        onClick={onRetry}
        data-testid="overview-retry-failed-cta"
        iconPosition="right"
        icon={<IconRefresh className="size-5 text-white" />}
      >
        <Trans i18nKey="photosOverview.uploadFailed.mainCta" />
      </Button>
    )
  }

  // Spinner rides along as the icon so the label stays readable, unlike the Button's own
  // `loading` prop which hides it
  if (mode === "retrying" || mode === "uploading") {
    return (
      <Button
        variant="primary"
        shape="wide"
        disabled
        data-testid="overview-uploading-cta"
        iconPosition="right"
        icon={<Spinner size="sm" />}
      >
        <Trans i18nKey="photosOverview.uploadingButton" />
      </Button>
    )
  }

  return (
    <Button
      variant="primary"
      shape="wide"
      onClick={onFinish}
      disabled={mode === "loading"}
      data-testid="overview-send-cta"
    >
      <Trans i18nKey="general.send" />
    </Button>
  )
}
